import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
  TextAnimate,
} from '@data-delivery/d3s';
import { SplunkRum } from '@splunk/otel-web';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { isRouteErrorResponse, useLocation, useNavigate } from 'react-router-dom';

interface FriendlyErrorProps {
  error: unknown;
  resetErrorBoundary: () => void;
  previousPath?: string;
}

function FriendlyErrorFallback({ error, resetErrorBoundary, previousPath }: FriendlyErrorProps) {
  const navigate = useNavigate();
  let status: string | undefined;
  let statusText: string | undefined;
  let message = 'Something went wrong.';

  if (isRouteErrorResponse(error)) {
    status = String(error.status);
    statusText = error.statusText;
    message = error.data?.message || error.statusText || message;
  } else if (error instanceof Error) {
    message = error.message;
  }

  SplunkRum.error(error);

  return (
    <div className='flex items-center justify-center py-10 px-6'>
      <Card className='max-w-xl w-full'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <TextAnimate
              text='Error Occurred'
              type='rollIn'
              className='text-xl font-semibold mb-0'
            />
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          {status && (
            <p className='text-sm font-medium'>
              Status: <span className='font-semibold'>{status}</span>
            </p>
          )}
          {statusText && (
            <p className='text-sm'>
              Status Text: <span className='font-medium'>{statusText}</span>
            </p>
          )}
          <Separator />
          <div>
            <h3 className='text-sm font-semibold mb-1'>Details</h3>
            <p className='text-sm text-muted-foreground whitespace-pre-wrap'>{message}</p>
          </div>
          <Separator />
          <div className='flex flex-wrap gap-2'>
            <Button
              variant='default'
              size='sm'
              onClick={() => {
                if (previousPath) {
                  navigate(previousPath);
                } else {
                  navigate('/');
                }
              }}
            >
              Go Back
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                resetErrorBoundary();
              }}
            >
              Retry
            </Button>
            <Button
              variant='secondary'
              size='sm'
              onClick={() => {
                window.location.reload();
              }}
            >
              Full Reload
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface BackgroundState {
  [key: string]: unknown;
  backgroundLocation?: { pathname: string };
}

export function HierarchicalErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const state = location.state as BackgroundState | undefined;
  const previousPath = state?.backgroundLocation?.pathname;
  return (
    <ReactErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <FriendlyErrorFallback
          error={error}
          resetErrorBoundary={resetErrorBoundary}
          previousPath={previousPath}
        />
      )}
      onError={error => {
        SplunkRum.error(error);
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}

export const ErrorBoundary = HierarchicalErrorBoundary;

export function RootErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ReactErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <FriendlyErrorFallback
          error={error}
          resetErrorBoundary={resetErrorBoundary}
          previousPath={undefined}
        />
      )}
      onError={error => {
        SplunkRum.error(error);
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}
